import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

/**
 * Build full copilot payload for API calls (dashboard_id, since, until, searchFilters).
 * Consolidates filter logic from DashboardData for use in copilot chat.
 * @param {string|number} dashboardId
 * @param {URLSearchParams} [params] - defaults to window.location.search
 * @param {object} [searchState] - DashboardReducer.searchState for selectedDaysRange fallback
 */
export const buildCopilotPayload = (dashboardId, params = null, fallbackSearchState = null) => {
  const p = params || new URLSearchParams(window.location.search);

  const safeParse = (val, def = null) => {
    if (!p.has(val)) return def;
    try {
      return JSON.parse(p.get(val));
    } catch {
      return p.get(val) || def;
    }
  };

  const searchState = {
    chosenDate: p.has("match_date") ? p.get("match_date") : "",
    chosenDocRating: p.has("rating") ? p.get("rating") : "",
    chosenHotTerm: safeParse("hotterm", []),
    chosenIntent: p.has("intent") ? p.get("intent") : "",
    chosenKeywords: safeParse("keyword", []),
    chosenNPS: p.has("nps") ? p.get("nps") : "",
    chosenPlatformName: p.has("platform_id") ? p.get("platform_id") : "",
    chosenSentiment: p.has("sentiment") ? p.get("sentiment") : "",
    chosenTopTopic: safeParse("top_topics", []),
    chosenFraud: p.has("fraud") ? p.get("fraud") : "",
    dateRange: p.has("since") && p.has("until") ? [p.get("since"), p.get("until")] : [],
    selectedDaysRange: p.has("daysRange") ? p.get("daysRange") : fallbackSearchState?.selectedDaysRange || "last7d",
    topicsOperator: p.has("topics_operator") ? p.get("topics_operator") : "OR",
    pivotFilter: safeParse("pivotFilter", {}),
    pivots_operator: p.has("pivots_operator") ? p.get("pivots_operator") : "AND",
    decisionFilter: safeParse("decisionFilter", {}),
  };
  if (p.has("top_topics")) {
    if (p.has("child_others")) {
      searchState.top_topics_others = true;
      searchState.child_others = safeParse("child_others");
    } else if (p.has("parent_others")) {
      searchState.top_topics_others = true;
      searchState.parent_others = safeParse("parent_others");
    } else {
      searchState.top_topics_others = false;
    }
  }
  for (let l = 1; l <= 2; l++) {
    if (p.has(`level${l}Selection`)) {
      searchState[`level${l}Selection`] = safeParse(`level${l}Selection`);
    }
  }

  const payload = {
    dashboard_id: parseInt(dashboardId, 10),
    searchFilters: searchState,
  };
  if (p.has("since") && p.has("until")) {
    payload.since = p.get("since");
    payload.until = p.get("until");
  }
  return payload;
};

/**
 * Build minimal context for copilot panel based on current route and dashboard state.
 * Used by GlobalCopilotPanel for context-aware placeholder.
 */
export const useCopilotContext = () => {
  const { t } = useTranslation();
  const DashboardReducer = useSelector((state) => state.DashboardReducer);
  const pathname = window.location.pathname;
  const params = new URLSearchParams(window.location.search);

  const segments = pathname.split("/").filter(Boolean);
  const page = segments[1] || "home";
  const subPage = segments[2] || null;
  const dashboardId = segments[3] || DashboardReducer.dashboard?.ID;

  const dashboardName = DashboardReducer.dashboard?.Name || null;
  // Prefer the URL's `daysRange` (the canonical current filter, same source as
  // since/until the advisor actually queries) so the context chip matches the
  // answer. Fall back to redux / legacy param / default only when absent.
  const selectedDaysRange =
    params.get("daysRange") ||
    DashboardReducer?.searchState?.selectedDaysRange ||
    params.get("selectedDaysRange") ||
    "last7d";

  const filterLabels = [];
  if (params.has("keyword")) {
    try {
      const kw = JSON.parse(params.get("keyword"));
      if (Array.isArray(kw) && kw.length) {
        filterLabels.push(t("cx_gpt.filter_keyword", { count: kw.length }));
      }
    } catch (_) {}
  }
  if (params.has("pivotFilter")) filterLabels.push(t("cx_gpt.filter_pivot_active"));
  if (params.has("top_topics")) filterLabels.push(t("cx_gpt.filter_topics_selected"));

  // Structured page scope so the advisor can resolve "the current dashboard"
  // without asking (dashboard id + selected topics + explicit date range).
  let topTopics = [];
  if (params.has("top_topics")) {
    try {
      const parsed = JSON.parse(params.get("top_topics"));
      if (Array.isArray(parsed)) {
        topTopics = parsed
          .filter((tp) => tp && tp.id != null)
          .map((tp) => ({ id: tp.id, label: tp.label || null }));
      }
    } catch (_) {}
  }
  const since = params.get("since") || null;
  const until = params.get("until") || null;

  let pivotFilter = {};
  if (params.has("pivotFilter")) {
    try {
      const parsed = JSON.parse(params.get("pivotFilter"));
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        pivotFilter = parsed;
      }
    } catch (_) {}
  }

  let kpiTeamId = null;
  if (page === "global_executive") {
    try {
      kpiTeamId = sessionStorage.getItem("copilotKpiTeamId") || null;
    } catch (_) {}
  }

  const DATA_FILTER_PAGES = [
    "DashboardData",
    "combined-view",
    "topic-gap",
    "top-problems",
    "insights",
    "savedSearches",
    "journey",
  ];
  const ACTION_NAV_PAGES = [
    "myDashboards",
    "topicLibrary",
    "industryTopics",
    "survey",
    "alerts",
    "report",
    "followed",
    "journeys",
    "pivony-sonic",
    "advisor-voice",
  ];

  const pageType =
    page === "home"
      ? "home"
      : DATA_FILTER_PAGES.includes(subPage)
      ? "dataFilter"
      : ACTION_NAV_PAGES.includes(subPage)
      ? "actionNav"
      : "dataFilter";

  const suggestedQuestions = getSuggestedQuestions(
    pageType,
    !!dashboardId,
    subPage,
    page,
    t
  );

  return {
    page,
    subPage,
    dashboardId,
    dashboardName,
    selectedDaysRange,
    topTopics,
    since,
    until,
    pivotFilter,
    filterLabels,
    hasDashboard: !!dashboardId && !!dashboardName,
    pageType,
    suggestedQuestions,
    kpiTeamId,
    isKpiPage: page === "global_executive",
  };
};

function getSuggestedQuestions(pageType, hasDashboard, subPage, page, t) {
  const dataFilter = [
    t("cx_gpt.suggested_data_1"),
    t("cx_gpt.suggested_data_2"),
    t("cx_gpt.suggested_data_3"),
  ];
  const actionNav = [
    t("cx_gpt.suggested_action_1"),
    t("cx_gpt.suggested_action_2"),
    t("cx_gpt.suggested_action_3"),
  ];
  const home = [
    t("cx_gpt.suggested_home_1"),
    t("cx_gpt.suggested_home_2"),
    t("cx_gpt.suggested_home_3"),
  ];
  const report = [
    t("cx_gpt.suggested_report_1"),
    t("cx_gpt.suggested_report_2"),
    t("cx_gpt.suggested_report_3"),
  ];
  const insights = [
    t("cx_gpt.suggested_insights_1"),
    t("cx_gpt.suggested_insights_2"),
    t("cx_gpt.suggested_insights_3"),
  ];
  const alerts = [
    t("cx_gpt.suggested_alerts_1"),
    t("cx_gpt.suggested_alerts_2"),
    t("cx_gpt.suggested_alerts_3"),
  ];

  if (page === "advisor") return home;
  if (page === "ai-services" && subPage === "reports") return report;
  if (page === "report" || subPage === "report") return report;
  if (page === "insights" || subPage === "insights") return insights;
  if (page === "alerts" || subPage === "alerts") return alerts;
  if (page === "global_executive") {
    return [
      t(
        "team_kpi_view.advisorKpiSuggested1",
        "Create a Maxx Royal Bodrum F&B KPI"
      ),
      t(
        "team_kpi_view.advisorKpiSuggested2",
        "I want to create a KPI by selecting a dashboard, pivot, and category"
      ),
      t(
        "team_kpi_view.advisorKpiSuggested3",
        "Which topics can be added as KPIs for this team?"
      ),
    ];
  }
  if (pageType === "home") return home;
  if (pageType === "actionNav") return actionNav;
  return hasDashboard ? dataFilter : actionNav;
}
